import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Assessment, Delete, OpenInNew, TrendingUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { predictionService } from '../services/predictionService';
import { Prediction } from '../types';
import { Header } from '../components/layout/Header';
import { format } from 'date-fns';

export const PredictionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [predictionToDelete, setPredictionToDelete] = useState<Prediction | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadPredictions();
  }, []);

  const loadPredictions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await predictionService.getPredictions();
      setPredictions(data);
    } catch (err: any) {
      console.error('Failed to load predictions:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni az árazásokat');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (prediction: Prediction, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation when clicking delete
    setPredictionToDelete(prediction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!predictionToDelete) return;

    try {
      setDeleting(true);
      await predictionService.deletePrediction(predictionToDelete.id);

      // Remove from local state immediately for better UX
      setPredictions(prev => prev.filter(p => p.id !== predictionToDelete.id));

      setDeleteDialogOpen(false);
      setPredictionToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete prediction:', err);
      console.error('Error details:', {
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });

      let errorMessage = 'Nem sikerült törölni az árazást';

      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.error ||
                      err.response.data?.message ||
                      `Hiba (${err.response.status}): ${err.response.statusText}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Nem sikerült kapcsolódni a szerverhez. Kérjük, ellenőrizze az internetkapcsolatot.';
      } else {
        // Something else happened
        errorMessage = err.message || errorMessage;
      }

      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPredictionToDelete(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'RUNNING':
      case 'PENDING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp sx={{ fontSize: 32 }} /> Előrejelzések
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Cash-flow előrejelzések kezelése és megtekintése
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : predictions.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Még nincs előrejelzés
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Kezdje el az első előrejelzést!
            </Typography>
            <Button variant="contained" onClick={() => navigate('/pricing')}>
              Új Előrejelzés
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {predictions.length} előrejelzés
            </Typography>
            <List>
              {predictions.map((prediction) => (
                <ListItem
                  key={prediction.id}
                  button
                  onClick={() => navigate(`/predictions/${prediction.id}`)}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        edge="end"
                        aria-label="open"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/predictions/${prediction.id}`);
                        }}
                        color="primary"
                      >
                        <OpenInNew />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => handleDeleteClick(prediction, e)}
                        color="error"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    pr: 12, // Make room for action buttons
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" component="span">
                          {prediction.uploadedFile?.fileName || `Előrejelzés #${prediction.id}`}
                        </Typography>
                        <Chip
                          label={prediction.status}
                          size="small"
                          color={getStatusColor(prediction.status) as any}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span">
                        Modell: {prediction.modelName} • Létrehozva:{' '}
                        {format(new Date(prediction.createdAt), 'yyyy-MM-dd HH:mm')}
                        {prediction.completedAt && (
                          <> • Befejezve: {format(new Date(prediction.completedAt), 'yyyy-MM-dd HH:mm')}</>
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
            >
              <DialogTitle id="delete-dialog-title">
                Előrejelzés törlése
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-dialog-description">
                  Biztosan törölni szeretnéd az előrejelzést?
                  {predictionToDelete && (
                    <>
                      <br />
                      <strong>{predictionToDelete.uploadedFile?.fileName || `Előrejelzés #${predictionToDelete.id}`}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Modell: {predictionToDelete.modelName} • Létrehozva:{' '}
                        {format(new Date(predictionToDelete.createdAt), 'yyyy-MM-dd HH:mm')}
                      </Typography>
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteCancel} disabled={deleting}>
                  Mégse
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  color="error"
                  variant="contained"
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
                >
                  {deleting ? 'Törlés...' : 'Törlés'}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}
      </Container>
    </>
  );
};



